<template>
  <div
    class="filter-drawer flex flex-col py-5 px-3 bg-white fixed top-0 left-0
  z-50 h-full max-w-xs w-full transform transition ease-in-out duration-500 border-r
  border-gray-100"
    style="filter:drop-shadow(2px 4px 2px rgba(0,0,0,0.1));"
    :aria-hidden="!active"
  >
    <div class="input-groups flex flex-col overflow-auto pb-10">
      <div class="input-group w-full flex flex-row items-baseline p-2 lg:pb-6">
        <h1 class="text-3xl font-bold w-full">Preferences</h1>
        <button
          aria-label="Clear All Filters"
          class="w-auto h-auto whitespace-no-wrap px-2 py-2 underline focus:outline-none
          focus:shadow-outline-green rounded"
          :class="{'opacity-50': !hasFilters}"
          :disabled="!hasFilters"
          @click="clearFilters"
        >
          Clear all
        </button>
      </div>
      <div class="input-group w-full p-2 xl:py-6">
        <p class="block text-m font-medium leading-5 text-gray-700 text-left mb-1">
          Loan Type
        </p>
        <div class="relative z-0 flex flex-row items-center justify-center">
          <button
            type="button"
            class="relative items-center px-4 py-2 rounded-l-md border text-sm
            leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
            focus:shadow-outline-blue transition ease-in-out duration-150
            h-10 w-1/2"
            :class="selectedLoanTypes.includes('Graduate')
              ? 'bg-green-500 border-green-500 text-white'
              : 'bg-white text-gray-700 border-gray-300'"
            @click="setLoanType('Graduate')"
          >
            Graduate
          </button>
          <button
            type="button"
            class="relative items-center px-4 py-2 rounded-r-md border text-sm
            leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
            focus:shadow-outline-blue transition ease-in-out duration-150
            h-10 w-1/2"
            :class="selectedLoanTypes.includes('Undergraduate')
              ? 'bg-green-500 border-green-500 text-white'
              : 'bg-white text-gray-700 border-gray-300'"
            @click="setLoanType('Undergraduate')"
          >
            Undergraduate
          </button>
        </div>
      </div>
      <div class="input-group w-full p-2 xl:py-6">
        <p class="block text-m font-medium leading-5 text-gray-700 text-left mb-1">
          Credit Score
        </p>
        <multiselect
          v-model="$selectedCreditScores"
          track-by="score"
          label="score"
          placeholder="Your credit score"
          :multiple="true"
          :disabled="loading"
          :options="reversedCreditScores"
          @input="handleFilterChange"
        ></multiselect>
      </div>
      <div class="input-group w-full p-2 xl:py-6">
        <div class="flex flex-row">
          <p class="block text-m font-medium leading-5 text-gray-700 text-left mb-1
                          w-full">
            Do you have a Co-signer?
          </p>
          <ToggleInput v-model="$showCosignerCreditScores"/>
        </div>
        <multiselect
          v-model="$selectedCosignerCreditScores"
          track-by="score"
          label="score"
          placeholder="Your co-signer's credit score"
          :multiple="true"
          :disabled="loading || !showCosignerCreditScores"
          :options="reversedCosignerCreditScores"
          @input="handleFilterChange"
        ></multiselect>
      </div>
      <div class="input-group w-full p-2 xl:py-6">
        <p class="block text-m font-medium leading-5 text-gray-700 text-left mb-1">
          Fixed or Variable rate
        </p>

        <div class="relative z-0 flex flex-row items-center justify-center">
          <button
            type="button"
            class="relative items-center px-4 py-2 rounded-l-md border text-sm
            leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
            focus:shadow-outline-blue transition ease-in-out duration-150
            h-10 w-1/2"
            @click="setRateType(rateTypes.find(rateType => rateType.id === 2))"
            :class="selectedRateTypes.find(rateType => rateType.id === 2)
                ? 'bg-green-500 border-green-500 text-white'
                : 'bg-white text-gray-700 border-gray-300'"
          >
            Variable
          </button>
          <button
            type="button"
            class="relative items-center px-4 py-2 rounded-r-md border text-sm
            leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300
            focus:shadow-outline-blue transition ease-in-out duration-150
            h-10 w-1/2"
            @click="setRateType(rateTypes.find(rateType => rateType.id === 1))"
            :class="selectedRateTypes.find(rateType => rateType.id === 1)
                ? 'bg-green-500 border-green-500 text-white'
                : 'bg-white text-gray-700 border-gray-300'"
          >
            Fixed
          </button>
        </div>
      </div>
      <div class="input-group w-full p-2 lg:py-6">
        <p class="block text-m font-medium leading-5 text-gray-700 text-left mb-1">
          Repayment Plans
        </p>
        <multiselect
          v-model="$selectedRepaymentPlans"
          track-by="plan"
          label="plan"
          placeholder="Repayment Plans"
          :multiple="true"
          :disabled="loading"
          :options="repaymentPlans"
          @input="handleFilterChange"
        ></multiselect>
      </div>
      <div class="input-group w-full p-2 xl:py-6">
        <p class="block text-m font-medium leading-5 text-gray-700 text-left mb-1">
          Lenders (leave blank if you want all)
        </p>
        <multiselect
          v-model="$selectedLenders"
          track-by="name"
          label="name"
          placeholder="Lenders"
          :multiple="true"
          :disabled="loading"
          :options="lenders"
          @input="handleFilterChange"
        ></multiselect>
      </div>
      <div class="input-group absolute bottom-0 right-0 left-0 text-center mb-2 bg-white">
        <button
          @click="$emit('closeDrawer')"
          class="text-gray-500 py-1 px-5 focus:outline-none focus:shadow-outline-green rounded"
        >
          Close Filters
          <svg
            :key="`menuClosed`"
            class="h-4 w-4 inline"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  name: 'FilterDrawer',
  props: {
    active: {
      type: Boolean,
      default: () => false,
    },
  },
  computed: {
    $showCosignerCreditScores: {
      get() {
        return this.showCosignerCreditScores;
      },
      set(val) {
        if (val === false) {
          this.setSelectedValue({ key: 'CosignerCreditScores', val: [] });
        }
        this.toggleCosigners(val);
        this.handleFilterChange();
      },
    },
    $selectedLoanTypes: {
      get() {
        return this.selectedLoanTypes;
      },
      set(val) {
        this.setSelectedValue({ key: 'LoanTypes', val });
      },
    },
    $selectedCreditScores: {
      get() {
        return this.selectedCreditScores;
      },
      set(val) {
        this.setSelectedValue({ key: 'CreditScores', val });
      },
    },
    $selectedCosignerCreditScores: {
      get() {
        return this.selectedCosignerCreditScores;
      },
      set(val) {
        this.setSelectedValue({ key: 'CosignerCreditScores', val });
      },
    },
    $selectedUniversities: {
      get() {
        return this.selectedUniversities;
      },
      set(val) {
        this.setSelectedValue({ key: 'Universities', val });
      },
    },
    $selectedDegrees: {
      get() {
        return this.selectedDegrees;
      },
      set(val) {
        this.setSelectedValue({ key: 'Degrees', val });
      },
    },
    $selectedRateTypes: {
      get() {
        return this.selectedRateTypes;
      },
      set(val) {
        this.setSelectedValue({ key: 'RateTypes', val });
      },
    },
    $selectedRepaymentPlans: {
      get() {
        return this.selectedRepaymentPlans;
      },
      set(val) {
        this.setSelectedValue({ key: 'RepaymentPlans', val });
      },
    },
    $selectedLenders: {
      get() {
        return this.selectedLenders;
      },
      set(val) {
        this.setSelectedValue({ key: 'Lenders', val });
      },
    },
    hasFilters() {
      const filters = [
        this.selectedLoanTypes.length,
        this.selectedCreditScores.length,
        this.selectedCosignerCreditScores.length,
        this.selectedUniversities.length,
        this.selectedDegrees.length,
        this.selectedRateTypes.length,
        this.selectedRepaymentPlans.length,
        this.selectedLenders.length,
      ];
      return this.showCosignerCreditScores
        || filters.find((filter) => filter !== 0) != null;
    },
    ...mapGetters('base', [
      'reversedCreditScores',
      'reversedCosignerCreditScores',
    ]),
    ...mapState('base', [
      'loading',
      'universities',
      'creditScores',
      'cosignerCreditScores',
      'degrees',
      'rateTypes',
      'repaymentPlans',
      'lenders',
      'showCosignerCreditScores',
      'selectedLoanTypes',
      'selectedCreditScores',
      'selectedCosignerCreditScores',
      'selectedUniversities',
      'selectedDegrees',
      'selectedRateTypes',
      'selectedRepaymentPlans',
      'selectedLenders',
    ]),
  },
  mounted() {
    if (document.body) {
      document.body.addEventListener('click', this.closeDrawer);
    }
  },
  beforeDestroy() {
    if (document.body) {
      document.body.removeEventListener('click', this.closeDrawer);
    }
  },
  methods: {
    ...mapActions('base', [
      'setSelectedValue',
      'toggleCosigners',
    ]),
    closeDrawer() {
      this.$emit('closeDrawer');
    },
    handleFilterChange(payload) {
      this.$emit('filterChange', payload);
    },
    clearFilters() {
      this.toggleCosigners(false);
      [
        'LoanTypes',
        'CreditScores',
        'CosignerCreditScores',
        'Universities',
        'Degrees',
        'RateTypes',
        'RepaymentPlans',
        'Lenders',
      ].forEach((filter) => {
        this.setSelectedValue({ key: filter, val: [] });
      });
      this.handleFilterChange();
    },
    setLoanType(val) {
      if (this.selectedLoanTypes.includes(val)) {
        this.setSelectedValue({ key: 'LoanTypes', val: [] });
      } else {
        this.setSelectedValue({ key: 'LoanTypes', val: [val] });
      }
      this.handleFilterChange();
    },
    setRateType(val) {
      if (this.selectedRateTypes.includes(val)) {
        this.setSelectedValue({ key: 'RateTypes', val: [] });
      } else {
        this.setSelectedValue({ key: 'RateTypes', val: [val] });
      }
      this.handleFilterChange();
    },
  },
};
</script>

<style>
  .input-groups {
    transition: all .5s ease-in-out;
    animation-name:animateIn;
    animation-iteration-count: 1;
  }
  .filter-drawer[aria-hidden="true"] .input-groups {
    visibility:hidden;
  }
  @keyframes animateIn {
    from {
      opacity:0;
      visibility:hidden;
    }
    to {
      opacity:1;
      visibility:visible;
    }
  }
</style>
